import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Building2, FileText, Shield, Users, Briefcase, Award, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { publicApi } from '../lib/api';
import { useLanguage } from '../hooks/useLanguage';
import SEO from '../components/SEO';

const GovernmentContractsPage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    publicApi.trackEvent({ event_type: 'page_view', page: 'government-contracts' }).catch(() => {});
  }, []);

  const services = [
    "Government contract readiness assessments",
    "SAM.gov registration guidance",
    "Small business certification strategy (WOSB, SDVOSB, HUBZone)",
    "Subcontractor agreement drafting and negotiation",
    "Teaming agreements and joint venture structures",
    "Compliance and procurement risk review"
  ];

  const industries = [
    { name: "Construction", icon: Building2 },
    { name: "Engineering", icon: Scale },
    { name: "Infrastructure", icon: Building2 },
    { name: "Consulting", icon: Users },
    { name: "Technology", icon: FileText },
    { name: "Logistics", icon: Briefcase },
    { name: "Professional Services", icon: Award }
  ];

  const clientBenefits = [
    "Prepare for state and federal contracting opportunities",
    "Navigate procurement rules and compliance requirements",
    "Structure contracts that protect their interests",
    "Build legally sound business foundations for growth"
  ];

  return (
    <div data-testid="government-contracts-page">
      <SEO 
        title="Government Contracts & Procurement Strategy"
        description="Expert legal guidance for government contracting. Former federal procurement professional helping businesses compete for state and federal contracts."
      />
      
      {/* Hero Section */}
      <section className="bg-[#1F2A44] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-6">
                Practice Area
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif tracking-tight leading-[1.1] text-white mb-6">
                Government Contracts & Procurement Strategy
              </h1>
              <p className="text-xl text-white/80 leading-relaxed mb-8">
                A Lawyer Who Understands How Government Contracts Actually Work
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/book" data-testid="gov-cta-book">
                  <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-8 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                    Book a Consultation
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1766778746072-70377616df3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxmZWRlcmFsJTIwYnVpbGRpbmclMjBnb3Zlcm5tZW50JTIwcHJvY3VyZW1lbnR8ZW58MHx8fHwxNzc0MjAwNzEwfDA&ixlib=rb-4.1.0&q=85"
                alt="Capitol Building"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="bg-[#FDFCFA] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/5520330/pexels-photo-5520330.jpeg"
                alt="Business professionals reviewing contracts"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
                Unique Experience
              </p>
              <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
                Real-World Government Contracting Experience
              </h2>
              <div className="text-[#555555] leading-relaxed space-y-4">
                <p>
                  Attorney Jennifer Brantley brings a rare combination of experience as a contracts attorney, public sector advisor (California High-Speed Rail), and former federal (Air Force and US Army Corps of Engineers) procurement professional.
                </p>
                <p>
                  Before launching J Brantley Law, she advised government agencies on contracting, compliance, and procurement strategy. She now uses that experience to help businesses navigate the complex world of government contracting.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Help Clients Achieve */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              Client Results
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222]">
              How We Help Businesses
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {clientBenefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-[#FDFCFA] border border-[#E8E4DC]">
                <CheckCircle className="w-6 h-6 text-[#C2A46D] flex-shrink-0 mt-1" />
                <p className="text-[#555555]">{benefit}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#555555] mt-8 max-w-3xl mx-auto">
            Her guidance is especially valuable for companies pursuing government contracts, public infrastructure work, and regulated industries.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-[#F5F3EF] border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
                Our Services
              </p>
              <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-6">
                Procurement Strategy Services
              </h2>
              <p className="text-[#555555] mb-8">
                Attorney Brantley helps companies position themselves to compete for public contracts through comprehensive legal services:
              </p>
              <div className="space-y-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#1F2A44] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-[#555555]">{service}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-8 border border-[#E8E4DC] shadow-lg">
              <h3 className="text-xl font-serif text-[#222222] mb-6">
                The Government Procurement Process
              </h3>
              <div className="space-y-6">
                <div className="border-l-4 border-[#C2A46D] pl-4">
                  <h4 className="font-semibold text-[#222222] mb-2">Pre-Solicitation</h4>
                  <p className="text-sm text-[#555555]">Evaluate opportunities, research programs, assist with certifications, and register on vendor portals.</p>
                </div>
                <div className="border-l-4 border-[#1F2A44] pl-4">
                  <h4 className="font-semibold text-[#222222] mb-2">Solicitation & Competition</h4>
                  <p className="text-sm text-[#555555]">Risk assessments, proposal review, compliance with procurement rules, and protest strategy.</p>
                </div>
                <div className="border-l-4 border-[#C2A46D] pl-4">
                  <h4 className="font-semibold text-[#222222] mb-2">Contract Negotiation</h4>
                  <p className="text-sm text-[#555555]">Negotiate terms, review pricing and timelines, identify and mitigate risks.</p>
                </div>
                <div className="border-l-4 border-[#1F2A44] pl-4">
                  <h4 className="font-semibold text-[#222222] mb-2">Contract Administration</h4>
                  <p className="text-sm text-[#555555]">Compliance monitoring, scope changes, modifications, and ethics obligations.</p>
                </div>
                <div className="border-l-4 border-[#C2A46D] pl-4">
                  <h4 className="font-semibold text-[#222222] mb-2">Disputes</h4>
                  <p className="text-sm text-[#555555]">Resolve contract disputes, advise on litigation risk, and respond to suspension actions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="bg-white border-b border-[#E8E4DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="text-center mb-12">
            <p className="text-xs font-mono uppercase tracking-widest text-[#C2A46D] mb-4">
              Industries Served
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#222222] mb-4">
              Who Benefits From Our Services
            </h2>
            <p className="text-[#555555] max-w-2xl mx-auto">
              Our government contracting services are particularly valuable for companies operating in the following spaces:
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div key={index} className="text-center p-4 bg-[#FDFCFA] border border-[#E8E4DC] hover:border-[#C2A46D] transition-colors">
                  <Icon className="w-8 h-8 text-[#1F2A44] mx-auto mb-2" />
                  <p className="text-sm text-[#555555] font-medium">{industry.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1F2A44]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif text-white mb-6">
                Ready To Compete With Confidence?
              </h2>
              <p className="text-lg text-white/80 mb-8">
                Whether you're pursuing your first government contract or looking to expand your public sector presence, J Brantley Law provides the strategic legal guidance you need to compete—and win.
              </p>
              <Link to="/book">
                <Button className="bg-[#C2A46D] hover:bg-[#A88B55] text-[#1F2A44] px-10 py-4 rounded-sm text-sm font-medium tracking-wide uppercase">
                  Schedule a Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.pexels.com/photos/6950033/pexels-photo-6950033.jpeg"
                alt="Business meeting"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GovernmentContractsPage;
